import type { DataEditorRef } from "@glideapps/glide-data-grid";
import { useIsMutating, useQueryClient } from "@tanstack/react-query";
import { useMemo, useRef } from "react";
import { useIsMobile } from "@volca/ui/hooks/use-mobile";
import {
  useGetAiScriptSpreadsheet,
  useRecoverAiScriptWorkbookOperation,
} from "@/payroll/ai-script/query";
import type { AiScriptSpreadsheetViewerProps } from "./ai-script-spreadsheet-viewer-types";
import { createGridKeyboardHandlers } from "./grid-keyboard-handlers";
import type { BufferedManualRowEdits } from "./manual-row-edit-buffer";
import { getSheetContentBounds } from "./manual-row-utils";
import { createMutationErrorHandlers } from "./mutation-error-feedback";
import { getSelectedCellState } from "./selected-cell-state";
import { usePendingCellNavigation } from "./use-go-to-cell";
import { useGridDrawCallbacks } from "./use-grid-draw-callbacks";
import { useGridHighlightRegions } from "./use-grid-highlight-regions";
import { useGridRenderers } from "./use-grid-renderers";
import { useSelectionReporting } from "./use-selection-reporting";
import { useSelectionSummary } from "./use-selection-summary";
import { useSheetLayout } from "./use-sheet-layout";
import { useSheetPrefetch } from "./use-sheet-prefetch";
import { useSheetSearch } from "./use-sheet-search";
import { useViewerActiveSheet } from "./use-viewer-active-sheet";
import {
  useOpenCommentCardOnSelect,
  useViewerComments,
} from "./use-viewer-comments";
import { useViewerMutations } from "./use-viewer-mutations";
import { useViewerShortcuts } from "./use-viewer-shortcuts";
import { useViewerUiState } from "./use-viewer-ui-state";
import { useWorkbookClipboard } from "./use-workbook-clipboard";
import { useWorkbookHistory } from "./use-workbook-history";

export const useViewerState = ({
  businessId,
  runId,
  activeSheetId: controlledActiveSheetId,
  onActiveSheetChange,
  isGlobalSearchEnabled = true,
  onSelectionContextChange,
  onEditedCellSelectionChange,
  sidebarOpen = false,
  onMaximizedChange,
  isLocked: locked = false,
  readOnlySource,
  approvalStatus,
  commentsEnabled = false,
}: AiScriptSpreadsheetViewerProps) => {
  const isLocked = locked || !!readOnlySource;
  const queryClient = useQueryClient();
  const isMobile = useIsMobile();
  const viewerRef = useRef<HTMLDivElement>(null);
  const gridRef = useRef<DataEditorRef>(null);
  const gridWrapperRef = useRef<HTMLDivElement>(null);
  const bufferedManualRowEditsRef = useRef<BufferedManualRowEdits>(new Map());
  const { data, isLoading, error } = useGetAiScriptSpreadsheet(
    businessId,
    runId,
    readOnlySource,
  );
  const workbookOperation = useRecoverAiScriptWorkbookOperation(
    readOnlySource ? null : businessId,
    readOnlySource ? null : runId,
  );
  const pendingWorkbookMutations = useIsMutating({
    predicate: (mutation) => {
      const variables = mutation.state.variables as
        | { runId?: string }
        | undefined;
      return variables?.runId === runId;
    },
  });
  const mutations = useViewerMutations(businessId);
  const history = useWorkbookHistory();
  const workbookVersionRef = useRef<number | undefined>(undefined);
  const ui = useViewerUiState({
    controlledActiveSheetId,
    onActiveSheetChange,
    onMaximizedChange,
    isMobile,
  });
  const sheet = useViewerActiveSheet({
    data,
    businessId,
    runId,
    readOnlySource,
    approvalStatus,
    isLocked,
    isMobile,
    gridWrapperRef,
    history,
    workbookVersionRef,
    ui,
  });
  const { activeSheet, activeLoadedSheet, adaptedSheet, regionRowMap } = sheet;
  const comments = useViewerComments({
    businessId,
    runId,
    commentsEnabled,
    isReadOnlySource: !!readOnlySource,
    sidebarOpen,
    gridRef,
    gridWrapperRef,
    sheet,
    ui,
  });
  const renderers = useGridRenderers({
    gridSelection: ui.gridSelection,
    hasActiveRegionView: sheet.hasActiveRegionView,
    regionRowMap,
  });

  const { maxContentRow, maxContentCol } = useMemo(
    () => getSheetContentBounds(activeLoadedSheet?.cells ?? []),
    [activeLoadedSheet],
  );

  const maxDisplayRow = sheet.hasActiveRegionView
    ? (adaptedSheet?.rowCount ?? maxContentRow)
    : maxContentRow;

  const clipboard = useWorkbookClipboard({
    activeSheetId: sheet.activeSheetValue,
    adaptedSheet,
    gridSelection: ui.gridSelection,
  });
  const isActiveSheetLoading =
    !!activeSheet && !activeSheet.empty && !adaptedSheet;
  const isActiveSheetEmpty =
    activeSheet?.empty || activeLoadedSheet?.cells.length === 0;
  const prefetchSheet = useSheetPrefetch({
    businessId: readOnlySource ? null : businessId,
    runId,
    sheets: sheet.sheets,
    activeSheetId: activeSheet?.id ?? null,
  });
  const errorHandlers = createMutationErrorHandlers({
    queryClient,
    businessId,
    runId,
  });
  const sheetLayout = useSheetLayout({
    businessId,
    runId,
    sheet: activeLoadedSheet,
    editable:
      sheet.canMutateActiveSheet ||
      (!locked && !!activeLoadedSheet?.layout_editable),
    source: readOnlySource,
    columns: adaptedSheet?.columns,
    onError: errorHandlers.handleMutationError,
  });
  const { columns } = sheetLayout;
  const keyboard = createGridKeyboardHandlers({
    columnCount: columns.length,
    gridRef,
    clipboard,
    sheet,
    ui,
  });
  const selectionSummary = useSelectionSummary({
    activeSheetId: activeSheet?.id,
    adaptedSheet,
    gridSelection: ui.gridSelection,
  });
  const selected = getSelectedCellState({
    gridSelection: ui.gridSelection,
    adaptedSheet,
    canMutateActiveSheet: sheet.canMutateActiveSheet,
  });
  useOpenCommentCardOnSelect({
    comments,
    selectedCol: selected.selectedCol,
    selectedRow: selected.selectedRow,
    activeLoadedSheetId: activeLoadedSheet?.id,
    ui,
  });
  const search = useSheetSearch({
    activeSheetId: activeSheet?.id ?? null,
    adaptedSheet,
  });
  const gridHighlightRegions = useGridHighlightRegions(
    ui.regionPreview,
    search.highlightRegions,
  );
  const isViewerShortcutScopeActive = useViewerShortcuts({
    viewerRef,
    isMaximized: ui.isMaximized,
    setIsMaximized: ui.setIsMaximized,
    isSheetSwitcherOpen: ui.isSheetSwitcherOpen,
    setIsSheetSwitcherOpen: ui.setIsSheetSwitcherOpen,
    canOpenSheetSearch:
      isGlobalSearchEnabled &&
      !sheet.isTooNarrowForGrid &&
      !sheet.activeSheetError &&
      !isActiveSheetLoading &&
      !isActiveSheetEmpty &&
      !!adaptedSheet,
    openSheetSearch: search.open,
  });
  useSelectionReporting({
    gridSelection: ui.gridSelection,
    adaptedSheet,
    activeLoadedSheet,
    regionRowMap,
    onSelectionContextChange,
    onEditedCellSelectionChange,
  });
  usePendingCellNavigation({
    activeSheetId: activeSheet?.id,
    activeSheetValue: sheet.activeSheetValue,
    adaptedSheet,
    hasActiveRegionView: sheet.hasActiveRegionView,
    regionRowMap,
    gridWrapperRef,
    ui,
  });
  const draw = useGridDrawCallbacks({
    activeRegionViewStates: sheet.activeRegionViewStates,
    activeRegions: sheet.activeRegions,
    activeSheetValue: sheet.activeSheetValue,
    adaptedSheet,
    columns,
    copiedSelection: clipboard.copiedSelection,
    frozenColumn: sheet.frozenColumn,
    isColumnInSelectedRange: renderers.isColumnInSelectedRange,
    comments,
  });

  return {
    isLocked,
    isLoading,
    error,
    queryClient,
    viewerRef,
    gridRef,
    gridWrapperRef,
    bufferedManualRowEditsRef,
    workbookOperation,
    pendingWorkbookMutations,
    mutations,
    history,
    workbookVersionRef,
    ui,
    sheet,
    comments,
    renderers,
    maxContentRow,
    maxContentCol,
    maxDisplayRow,
    clipboard,
    isActiveSheetLoading,
    isActiveSheetEmpty,
    prefetchSheet,
    errorHandlers,
    sheetLayout,
    columns,
    keyboard,
    selectionSummary,
    selected,
    search,
    gridHighlightRegions,
    isViewerShortcutScopeActive,
    draw,
  };
};

export type ViewerState = ReturnType<typeof useViewerState>;
